import { useCallback, useContext, useRef, useState } from "react";
import { AppContext } from "../context/AppContext";
import useHttp from "./useHttp.jsx";

const DEBOUNCE_MS = 400;
const MIN_QUERY_LENGTH = 2;
const PAGE_SIZE = 10;

export default function useGlobalSearch() {
    const [appProperties] = useContext(AppContext);
    const { fetchDataByPagination } = useHttp();
    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState(null);
    const debounceRef = useRef(null);
    const lastQueryRef = useRef("");
    const lastTypeRef = useRef("all");
    const abortControllerRef = useRef(null);

    const fetchPage = useCallback(
        async (query, type, start, signal) => {
            const typeParam = !type || type === "all" ? "" : `&type=${encodeURIComponent(type)}`;
            const baseUrl = `search?q=${encodeURIComponent(query.trim())}${typeParam}`;
            const response = await fetchDataByPagination(
                baseUrl,
                "GET",
                null,
                appProperties,
                null,
                PAGE_SIZE,
                { start },
                null,
                signal
            );
            const data = response?.data ?? response;
            return Array.isArray(data) ? data : [];
        },
        [fetchDataByPagination, appProperties]
    );

    const search = useCallback(
        (query, type = "all") => {
            if (!query || typeof query !== "string" || query.trim().length < MIN_QUERY_LENGTH) {
                setResults([]);
                setHasMore(false);
                return;
            }
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
            debounceRef.current = setTimeout(async () => {
                abortControllerRef.current?.abort();
                const controller = new AbortController();
                abortControllerRef.current = controller;

                lastQueryRef.current = query.trim();
                lastTypeRef.current = type || "all";
                setIsLoading(true);
                setError(null);
                try {
                    const data = await fetchPage(query, type, 0, controller.signal);
                    if (controller.signal.aborted) return;
                    setResults(data);
                    setHasMore(data.length >= PAGE_SIZE);
                } catch (e) {
                    if (controller.signal.aborted) return;
                    setError(e);
                    setResults([]);
                    setHasMore(false);
                } finally {
                    if (!controller.signal.aborted) {
                        setIsLoading(false);
                    }
                    debounceRef.current = null;
                }
            }, DEBOUNCE_MS);
        },
        [fetchPage]
    );

    const loadMore = useCallback(async () => {
        const query = lastQueryRef.current;
        const type = lastTypeRef.current;
        if (!query || isLoading || isLoadingMore || !hasMore) return;

        const controller = new AbortController();
        setIsLoadingMore(true);
        try {
            const data = await fetchPage(query, type, results.length, controller.signal);
            if (controller.signal.aborted) return;
            setResults((prev) => [...prev, ...data]);
            setHasMore(data.length >= PAGE_SIZE);
        } catch (e) {
            if (controller.signal.aborted) return;
            setHasMore(false);
        } finally {
            if (!controller.signal.aborted) {
                setIsLoadingMore(false);
            }
        }
    }, [fetchPage, results.length, isLoading, isLoadingMore, hasMore]);

    const clearResults = useCallback((isFilterChange = false) => {
        setResults([]);
        setHasMore(false);
        setError(null);
        if (!isFilterChange) {
            lastQueryRef.current = "";
        }
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
    }, []);

    return { search, results, isLoading, setIsLoading, isLoadingMore, hasMore, error, loadMore, clearResults };
}
